'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

const EASE = [0.23, 1, 0.32, 1] as const;
const COLORS = ['#6ee7c7', '#34d399', '#5eead4'];

// useLayoutEffect on the client (measures before first paint), useEffect on SSR.
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// The field is a fixed pixel grid; each cell deterministically owns 0..1 mote.
// Because a cell's position is deterministic, growing the page (e.g. expanding
// the contact textarea) only ever ADDS motes in the new space — existing ones
// keep their pixel positions instead of stretching with the page.
const CELL = 66; // px
const BASE = 0.10; // ambient density everywhere (so the whole page has dust)
const BOOST = 0.62; // extra density at the delta
const SIGMA = 230; // px falloff radius from the delta

// Deterministic per-cell hash → [0, 1). Same (cx, cy, salt) always yields the
// same value, so cells are stable across re-measures.
function cellRand(cx: number, cy: number, salt: number): number {
  let h = (Math.imul(cx, 374761393) + Math.imul(cy, 668265263) + Math.imul(salt, 2147483647)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) | 0;
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

type Mote = {
  key: string;
  left: number;
  top: number;
  size: number;
  color: string;
  delay: number;
  dur: number;
  peak: number;
};

function buildField(w: number, h: number, coreX: number, coreY: number): Mote[] {
  const cols = Math.ceil(w / CELL);
  const rows = Math.ceil(h / CELL);
  const out: Mote[] = [];
  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      const ccx = cx * CELL + CELL / 2;
      const ccy = cy * CELL + CELL / 2;
      const d = Math.hypot(ccx - coreX, ccy - coreY);
      const density = BASE + BOOST * Math.exp(-(d * d) / (2 * SIGMA * SIGMA));
      if (cellRand(cx, cy, 1) > density) continue;
      out.push({
        key: `${cx}-${cy}`,
        left: +(cx * CELL + cellRand(cx, cy, 3) * CELL).toFixed(1),
        top: +(cy * CELL + cellRand(cx, cy, 4) * CELL).toFixed(1),
        size: +(0.8 + cellRand(cx, cy, 2) * 1.8).toFixed(2),
        color: COLORS[Math.floor(cellRand(cx, cy, 5) * COLORS.length)],
        delay: +(cellRand(cx, cy, 6) * 6).toFixed(2),
        dur: +(4 + cellRand(cx, cy, 7) * 5).toFixed(2),
        peak: +(0.35 + cellRand(cx, cy, 8) * 0.5).toFixed(2),
      });
    }
  }
  return out;
}

/**
 * Site-wide cosmic background, anchored to the PAGE (scrolls with content).
 * Subtle neutral depth + a green bloom at the delta, and a galaxy of dust laid
 * out on a deterministic pixel grid: dense (Gaussian) at the delta's measured
 * position, ambient elsewhere. Growing the page adds dust in the new space
 * without moving existing dust. Fades in on load. Reduced-motion aware.
 */
export default function CosmicBackground() {
  const reduce = useReducedMotion();
  const fieldRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef({ w: 0, h: 0, coreX: 0, coreY: 0 });
  const [dims, setDims] = useState({ w: 0, h: 0, coreX: 0, coreY: 0 });

  useIsoLayoutEffect(() => {
    const measure = () => {
      const field = fieldRef.current;
      if (!field) return;
      const fr = field.getBoundingClientRect();
      const w = fr.width;
      const h = fr.height;
      let coreX = w / 2;
      let coreY = h * 0.1;
      const glyph = document.getElementById('delta-glyph');
      if (glyph) {
        const gr = glyph.getBoundingClientRect();
        coreX = gr.left + gr.width / 2 - fr.left;
        coreY = gr.top + gr.height / 2 - fr.top;
      }
      const p = prevRef.current;
      if (p.w !== w || p.h !== h || Math.abs(p.coreX - coreX) > 1 || Math.abs(p.coreY - coreY) > 1) {
        prevRef.current = { w, h, coreX, coreY };
        setDims({ w, h, coreX, coreY });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (fieldRef.current) ro.observe(fieldRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const motes = useMemo(
    () => (dims.w && dims.h ? buildField(dims.w, dims.h, dims.coreX, dims.coreY) : []),
    [dims],
  );

  return (
    <div
      ref={fieldRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
    >
      <div className="absolute inset-0 delta-depth" />
      <div className="absolute inset-0 delta-bloom" />

      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: reduce ? 0 : 0.3, duration: reduce ? 0.6 : 2, ease: EASE }}
      >
        {motes.map((m) => (
          <span
            key={m.key}
            className="cosmic-dust"
            style={
              {
                left: `${m.left}px`,
                top: `${m.top}px`,
                width: `${m.size}px`,
                height: `${m.size}px`,
                background: m.color,
                boxShadow: `0 0 ${m.size * 2.2}px ${m.size * 0.6}px ${m.color}`,
                animationDelay: `${m.delay}s`,
                animationDuration: `${m.dur}s`,
                '--peak': m.peak,
              } as CSSProperties
            }
          />
        ))}
      </motion.div>
    </div>
  );
}
